import React, { useMemo, useState } from "react";

import {
  Button,
  Card,
  Col,
  List,
  Progress,
  Row,
  Space,
  Tag,
  Typography,
  Upload,
  Tooltip,
} from "antd";
import {
  ClockCircleOutlined,
  InfoCircleOutlined,
  LineChartOutlined,
  TrophyOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import DashboardLayout from "../components/DashboardLayout";
import PageLoader from "../components/PageLoader";
import profileIcon from "../assets/profile.png";
import LogrosCarousel from "../components/LogrosCarousel";
import ReactTimeAgo from "react-time-ago";
import useCurrentUserData from "../hooks/useCurrentUserData";
import useRewardsCatalog from "../hooks/useRewardsCatalog";
import "react-time-ago/locale/es";
import RewardedProfileDisplay from "../components/RewardedProfileDisplay";
import { renderLooseNode, mergeRewardsWithUserState } from "../utils/rewards";
import { UpdateEntryImg } from "../firebase-config";
import { useAuth } from "../contexts/AuthContext";
import { getExpProgress, getLevelFromExp } from "../utils/levels";
import {
  AVERAGE_SKILL_HELP,
  getAverageSkillPercent,
} from "../utils/skillUtils";
import { dashboardPalette } from "../theme";
import XpInfoModal from "../components/XpInfo";

const ProfilePage: React.FC = () => {
  const { userData, isLoading: userLoading } = useCurrentUserData();
  const { catalog, isLoading: catalogLoading } = useRewardsCatalog();
  const { user } = useAuth();
  const [xpInfoOpen, setXpInfoOpen] = useState(false);
  const userSkills = userData?.Skill ?? [];
  const userNotifications = userData?.Notifications ?? [];
  const currentExp = userData?.CurrentExp ?? 0;
  const derivedLevel = getLevelFromExp(currentExp);
  const currentExpProgress = getExpProgress(derivedLevel, currentExp);
  const mergedRewards = useMemo(
    () => mergeRewardsWithUserState(catalog, userData?.Recompensas),
    [catalog, userData?.Recompensas],
  );

  const handleUpload = (file: File) => {
    if (!file || !user) {
      return false;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const imageValue = typeof reader.result === "string" ? reader.result : "";
      if (!imageValue) return;
      void UpdateEntryImg(user.uid, imageValue);
    };

    return false;
  };

  const averageSkill = getAverageSkillPercent(userSkills);

  if ((userLoading && !userData) || (catalogLoading && !catalog.length)) {
    return (
      <DashboardLayout>
        <PageLoader minHeight={480} />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div style={{ maxWidth: 1280, margin: "0 auto", width: "100%" }}>
        <Card
          className="app-glass hero-panel"
          style={{
            borderRadius: 24,
            border: "1px solid var(--dashboard-panel-border)",
            boxShadow: "0 16px 40px rgba(10, 19, 53, 0.18)",
          }}
          bodyStyle={{ padding: 24 }}
        >
          <Row gutter={[20, 20]} align="middle">
            <Col xs={24} md={10} lg={8}>
              <Space
                direction="vertical"
                size={14}
                style={{ width: "100%", alignItems: "center" }}
              >
                <RewardedProfileDisplay
                  src={userData?.PortraitImg}
                  alt="Perfil"
                  fallbackSrc={profileIcon}
                  width={160}
                  height={160}
                  border={`4px solid ${dashboardPalette.headerBlue}`}
                  boxShadow="0 8px 0 rgba(47,43,116,0.18)"
                  backgroundColor="#fff4c4"
                  rewards={[...mergedRewards, ...(userData?.Items ?? [])]}
                  overlayMode="full"
                  overlayImageSx={{ top: -14 }}
                  badgeSize={26}
                  overlaySize={32}
                />

                <Upload
                  beforeUpload={handleUpload}
                  showUploadList={false}
                  accept="image/png, image/gif, image/jpeg, image/jpg"
                >
                  <Button
                    icon={<UploadOutlined />}
                    style={{ borderRadius: 14, fontWeight: 800 }}
                  >
                    Subir imagen
                  </Button>
                </Upload>
              </Space>
            </Col>

            <Col xs={24} md={14} lg={16}>
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Typography.Title
                  level={2}
                  style={{ margin: 0, color: "var(--dashboard-header-blue)" }}
                >
                  {userData?.Nombre || "Usuario"}
                </Typography.Title>
                <Typography.Text style={{ color: "var(--dashboard-text)" }}>
                  {userData?.Email}
                </Typography.Text>

                <Space wrap>
                  <Tag color="gold">Nivel {derivedLevel}</Tag>
                  <Tag color="green">{userData?.CurrentExp ?? 0} XP</Tag>
                  <Tag color="blue">{userData?.Logros?.length ?? 0} Logros</Tag>
                </Space>

                <div
                  style={{
                    borderRadius: 18,
                    padding: 16,
                    background: "rgba(255,255,255,0.7)",
                    border: `1px solid ${dashboardPalette.panelBorder}`,
                  }}
                >
                  <Space
                    direction="vertical"
                    size={8}
                    style={{ width: "100%" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 12,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography.Text
                        strong
                        style={{ color: dashboardPalette.headerBlue }}
                      >
                        Progreso al siguiente nivel
                      </Typography.Text>
                      <Button
                        type="text"
                        size="small"
                        icon={<InfoCircleOutlined />}
                        onClick={() => setXpInfoOpen(true)}
                        style={{
                          fontWeight: 800,
                          color: dashboardPalette.headerBlue,
                        }}
                      >
                        ¿Cómo gano XP?
                      </Button>
                    </div>
                    <Progress
                      percent={currentExpProgress}
                      strokeColor="var(--dashboard-teal)"
                      trailColor="var(--dashboard-track)"
                      format={(percent) =>
                        `${Number(percent ?? 0).toFixed(2)}%`
                      }
                    />
                  </Space>
                </div>
              </Space>
            </Col>
          </Row>
        </Card>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={16}>
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
              <Card
                className="app-glass"
                style={{
                  borderRadius: 24,
                  border: "1px solid var(--dashboard-panel-border)",
                  boxShadow: "0 16px 40px rgba(10, 19, 53, 0.18)",
                }}
                bodyStyle={{ padding: 20 }}
              >
                <Typography.Title
                  level={4}
                  style={{
                    marginTop: 0,
                    color: "var(--dashboard-header-blue)",
                  }}
                >
                  <LineChartOutlined /> Estadísticas generales
                </Typography.Title>

                <Row gutter={[12, 12]}>
                  <Col xs={12} sm={8}>
                    <div
                      style={{
                        borderRadius: 18,
                        padding: 16,
                        textAlign: "center",
                        background: "rgba(255,255,255,0.72)",
                        border: `1px solid ${dashboardPalette.panelBorder}`,
                      }}
                    >
                      <Typography.Text
                        strong
                        style={{ color: dashboardPalette.headerBlue }}
                      >
                        Skills
                      </Typography.Text>
                      <Typography.Title
                        level={2}
                        style={{ margin: 0, color: dashboardPalette.teal }}
                      >
                        {userSkills.length}
                      </Typography.Title>
                    </div>
                  </Col>
                  <Col xs={12} sm={8}>
                    <div
                      style={{
                        borderRadius: 18,
                        padding: 16,
                        textAlign: "center",
                        background: "rgba(255,255,255,0.72)",
                        border: `1px solid ${dashboardPalette.panelBorder}`,
                      }}
                    >
                      <Typography.Text
                        strong
                        style={{ color: dashboardPalette.headerBlue }}
                      >
                        Promedio de skills{" "}
                        <Tooltip title={AVERAGE_SKILL_HELP}>
                          <InfoCircleOutlined style={{ fontSize: 12 }} />
                        </Tooltip>
                      </Typography.Text>
                      <Typography.Title
                        level={2}
                        style={{ margin: 0, color: "#ff6ddd" }}
                      >
                        {averageSkill}%
                      </Typography.Title>
                    </div>
                  </Col>
                  <Col xs={12} sm={8}>
                    <div
                      style={{
                        borderRadius: 18,
                        padding: 16,
                        textAlign: "center",
                        background: "rgba(255,255,255,0.72)",
                        border: `1px solid ${dashboardPalette.panelBorder}`,
                      }}
                    >
                      <Typography.Text
                        strong
                        style={{ color: dashboardPalette.headerBlue }}
                      >
                        Logros
                      </Typography.Text>
                      <Typography.Title
                        level={2}
                        style={{ margin: 0, color: "#f7c50b" }}
                      >
                        {userData?.Logros?.length ?? 0}
                      </Typography.Title>
                    </div>
                  </Col>
                </Row>
              </Card>

              <Card
                className="app-glass"
                style={{
                  borderRadius: 24,
                  border: "1px solid var(--dashboard-panel-border)",
                  boxShadow: "0 16px 40px rgba(10, 19, 53, 0.18)",
                }}
                bodyStyle={{ padding: 20 }}
              >
                <Typography.Title
                  level={4}
                  style={{
                    marginTop: 0,
                    color: "var(--dashboard-header-blue)",
                  }}
                >
                  <TrophyOutlined /> Logros
                </Typography.Title>
                {userData && userData.Logros.length >= 1 ? (
                  <LogrosCarousel />
                ) : (
                  <Typography.Text>No hay logros para mostrar.</Typography.Text>
                )}
              </Card>
            </Space>
          </Col>

          <Col xs={24} lg={8}>
            <Card
              className="app-glass"
              style={{
                height: "100%",
                borderRadius: 24,
                border: "1px solid var(--dashboard-panel-border)",
                boxShadow: "0 16px 40px rgba(10, 19, 53, 0.18)",
              }}
              bodyStyle={{ padding: 20 }}
            >
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Typography.Title
                  level={4}
                  style={{
                    marginTop: 0,
                    color: "var(--dashboard-header-blue)",
                  }}
                >
                  <ClockCircleOutlined /> Actividad reciente
                </Typography.Title>

                <List
                  dataSource={userNotifications}
                  renderItem={(notif) => (
                    <List.Item style={{ paddingInline: 0 }}>
                      <Card
                        size="small"
                        style={{
                          width: "100%",
                          borderRadius: 16,
                          border: `1px solid ${dashboardPalette.panelBorder}`,
                          background: "rgba(255,255,255,0.72)",
                        }}
                      >
                        <Space align="start" size={12}>
                          <div
                            style={{
                              width: 34,
                              height: 34,
                              borderRadius: "50%",
                              display: "grid",
                              placeItems: "center",
                              background: "var(--dashboard-yellow)",
                              color: "var(--dashboard-header-blue)",
                            }}
                          >
                            {renderLooseNode(
                              notif.Icon,
                              <ClockCircleOutlined />,
                            )}
                          </div>
                          <div>
                            <Typography.Text
                              strong
                              style={{
                                display: "block",
                                color: dashboardPalette.headerBlue,
                              }}
                            >
                              {notif.Title}
                            </Typography.Text>
                            <Typography.Text
                              style={{ color: dashboardPalette.textDark }}
                            >
                              {notif.Date ? (
                                <ReactTimeAgo
                                  date={notif.Date.seconds * 1000}
                                  locale="es"
                                />
                              ) : (
                                "Sin fecha"
                              )}
                            </Typography.Text>
                          </div>
                        </Space>
                      </Card>
                    </List.Item>
                  )}
                />
              </Space>
            </Card>
          </Col>
        </Row>
      </div>
      <XpInfoModal open={xpInfoOpen} onClose={() => setXpInfoOpen(false)} />
    </DashboardLayout>
  );
};

export default ProfilePage;
